import { supabase } from '../lib/supabase';

export interface PartnerProspect {
  id: string;
  business_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  business_address: string;
  business_type: string;
  status: 'pending' | 'pre_approved' | 'active' | 'rejected';
  application_date: string;
  calendar_link_sent_at: string | null;
  calendar_slot_selected: boolean;
  selected_time_slot: string | null;
  notes: string | null;
  created_at: string;
}

export interface PipelineMetrics {
  total_prospects: number;
  qualified_leads: number;
  conversion_rate: number;
  avg_deal_closure_time: number;
}

export const partnerAcquisitionService = {
  /**
   * Fetch all partner prospects with optional filtering
   */
  async getPartnerProspects(filters?: {
    status?: string;
    district?: string;
    leadSource?: string;
  }): Promise<PartnerProspect[]> {
    try {
      let query = supabase
        .from('partners')
        .select('*')
        .order('application_date', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching partner prospects:', error);
      throw error;
    }
  },

  /**
   * Get pipeline metrics for KPIs
   */
  async getPipelineMetrics(): Promise<PipelineMetrics> {
    try {
      const { data: allPartners, error } = await supabase
        .from('partners')
        .select('*');

      if (error) throw error;

      const partners = allPartners || [];
      const total_prospects = partners.length;
      const qualified_leads = partners.filter(p => 
        p.status === 'pre_approved' || p.status === 'active'
      ).length;
      const conversion_rate = total_prospects > 0 
        ? (qualified_leads / total_prospects) * 100 
        : 0;

      // Calculate average deal closure time
      const activePartners = partners.filter(p => p.status === 'active' && p.approved_date);
      let avg_deal_closure_time = 0;
      
      if (activePartners.length > 0) {
        const totalDays = activePartners.reduce((sum, partner) => {
          const appDate = new Date(partner.application_date);
          const approvedDate = new Date(partner.approved_date);
          const days = Math.floor((approvedDate.getTime() - appDate.getTime()) / (1000 * 60 * 60 * 24));
          return sum + days;
        }, 0);
        avg_deal_closure_time = totalDays / activePartners.length;
      }

      return {
        total_prospects,
        qualified_leads,
        conversion_rate: Math.round(conversion_rate * 10) / 10,
        avg_deal_closure_time: Math.round(avg_deal_closure_time)
      };
    } catch (error) {
      console.error('Error fetching pipeline metrics:', error);
      throw error;
    }
  },

  /**
   * Update partner prospect stage/status
   */
  async updateProspectStage(
    partnerId: string,
    newStatus: string,
    notes?: string
  ): Promise<void> {
    try {
      const updateData: any = { 
        status: newStatus,
        updated_at: new Date().toISOString()
      };
      
      if (notes) {
        updateData.notes = notes;
      }

      const { error } = await supabase
        .from('partners')
        .update(updateData)
        .eq('id', partnerId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating prospect stage:', error);
      throw error;
    }
  },

  /**
   * Assign sales representative to a prospect
   */
  async assignSalesRep(partnerId: string, userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('partners')
        .update({ 
          created_by: userId,
          updated_at: new Date().toISOString()
        })
        .eq('id', partnerId);

      if (error) throw error;
    } catch (error) {
      console.error('Error assigning sales rep:', error);
      throw error;
    }
  },

  /**
   * Get lead scoring data
   */
  async getLeadScoring(partnerId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('partners')
        .select('*')
        .eq('id', partnerId)
        .single();

      if (error) throw error;

      // Simple lead scoring algorithm
      let score = 0;
      
      if (data?.business_license_number) score += 20;
      if (data?.website_url) score += 15;
      if (data?.business_description) score += 10;
      if (data?.expected_customer_volume) score += 15;
      if (data?.marketing_goals) score += 10;
      if (data?.calendar_slot_selected) score += 30;

      return Math.min(score, 100);
    } catch (error) {
      console.error('Error calculating lead score:', error);
      return 0;
    }
  }
};

/**
 * Create partner account with magic link authentication
 */
export async function createPartnerAccount(applicationId: string): Promise<{
  success: boolean;
  partnerId?: string;
  readableId?: string;
  message: string;
}> {
  try {
    // Get application details
    const { data: application, error: appError } = await supabase
      .from('partner_applications')
      .select('*')
      .eq('id', applicationId)
      .single();

    if (appError || !application) {
      throw new Error('Application not found');
    }

    // Send magic link for authentication
    const { error: magicLinkError } = await supabase.auth.signInWithOtp({
      email: application.contact_email,
      options: {
        emailRedirectTo: `${window.location.origin}/partner-login`,
        data: {
          business_name: application.business_name,
          contact_name: application.contact_name,
          role: 'partner'
        }
      }
    });

    if (magicLinkError) {
      throw new Error(`Failed to send magic link: ${magicLinkError.message}`);
    }

    // Create partner account (auth.users will be created when they click the magic link)
    const { data: partner, error: partnerError } = await supabase
      .rpc('create_partner_account', {
        p_business_name: application.business_name,
        p_contact_email: application.contact_email,
        p_contact_name: application.contact_name,
        p_contact_phone: application.contact_phone || '',
        p_business_address: application.business_address || '',
        p_category: application.category,
        p_application_id: applicationId
      });

    if (partnerError) {
      throw new Error(`Failed to create partner account: ${partnerError.message}`);
    }

    return {
      success: true,
      partnerId: partner?.partner_id,
      readableId: partner?.readable_id,
      message: `Magic link sent successfully to ${application.contact_email}. Partner will be able to login once they verify their email.`
    };
  } catch (error: any) {
    console.error('Partner account creation error:', error);
    return {
      success: false,
      message: error.message || 'Failed to create partner account'
    };
  }
}